<template>
  <section class="eva-page">
    <v-row>
      <v-col cols="12" offset="0" md="10" offset-md="1">
        <div class="eva-top">
          <v-breadcrumbs
            large
            :items="crumbs"
            divider="/"
            class="pl-0 eva-breadcrumbs"
          />

          <div class="eva-titlebar">
            <div class="eva-title">
              <v-icon color="primary" class="mr-2">mdi-book-open-page-variant</v-icon>
              <span class="primary--text">Listagem de diários de secção</span>
            </div>
          </div>
        </div>

        <v-card class="eva-card mt-6" elevation="10">
          <v-card-title class="eva-card__title primary white--text">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="white">mdi-clipboard-text</v-icon>
              <span class="font-weight-bold">Diários de secção</span>
            </div>

            <v-spacer />

            <v-chip color="white" outlined class="eva-chip">
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              {{ total }} registos
            </v-chip>
          </v-card-title>

          <v-card-text class="pt-6">
            <div style="position:relative">
              <v-data-table
                :headers="headers"
                :items="items"
                :server-items-length="total"
                :loading="loading"
                :footer-props="tableFooter"
                :options.sync="options"
                class="eva-table"
                locale="pt-pt"
              >
                <template v-slot:top>
                  <div class="d-flex align-center justify-space-between px-4 py-2">
                    <div class="text-subtitle-1 font-weight-bold">
                      Diários de secção
                    </div>

                    <v-btn class="primary" small @click="$router.push('section-diaries/create')">
                      <v-icon left>mdi-plus</v-icon>
                      Adicionar
                    </v-btn>
                  </div>
                </template>

                <template v-slot:item.dia="{ item }">
                  {{ getTableDate(item.dia) }}
                </template>

                <template v-slot:item.creator="{ item }">
                  {{ item.createdTo ? item.createdTo.funcionario.Codigo + " - " + item.createdTo.name : "—" }}
                </template>

                <template v-slot:item.approved="{ item }">
                  <v-chip
                    small
                    :color="item.approved ? 'success' : 'warning'"
                    dark
                  >
                    <v-icon left small>
                      {{ item.approved ? "mdi-check-circle" : "mdi-clock-outline" }}
                    </v-icon>
                    {{ item.approved ? "Aprovado" : "Por aprovar" }}
                  </v-chip>
                </template>

                <template v-slot:item.linhas_count="{ item }">
                  {{ item.linhas ? item.linhas.length : 0 }}
                </template>

                <template v-slot:item.created_at="{ item }">
                  {{ getTableDate(item.created_at) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-row align="center" justify="center" no-gutters>
                    <v-col cols="auto">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            color="primary"
                            class="mr-2"
                            v-bind="attrs"
                            v-on="on"
                            @click="$router.push('section-diaries/' + item.id)"
                          >
                            mdi-eye
                          </v-icon>
                        </template>
                        <span>Ver</span>
                      </v-tooltip>
                    </v-col>

                    <v-col
                      v-if="canApproveSectionDiaries && !item.approved"
                      cols="auto"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            color="success"
                            class="mr-2"
                            v-bind="attrs"
                            v-on="on"
                            @click="approveSectionDiary(item)"
                          >
                            mdi-check-decagram
                          </v-icon>
                        </template>
                        <span>Aprovar</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </template>

                <template v-slot:no-data>
                  <div class="pa-6 grey--text text--darken-1">
                    Sem resultados.
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import SectionDiaries from "@/api/SectionDiaries.js";
import dayjs from "dayjs";

export default {
  watch: {
    options: {
      handler() {
        setTimeout(() => {
          this.searchSectionDiaries();
        }, 10);
      },
      deep: true,
    },
  },

  beforeCreate() {
    document.title = "ODC - Listagem de diários de secção";
  },

  data: () => ({
    headers: [
      { text: "Dia", sortable: true, value: "dia" },
      { text: "Colaborador", sortable: false, value: "creator" },
      { text: "Centro de Custo", sortable: false, value: "department_code" },
      { text: "Estado", sortable: false, value: "approved" },
      { text: "N.º Linhas", sortable: false, value: "linhas_count" },
      { text: "Criado em", sortable: true, value: "created_at" },
      { text: "Opções", align: "center", sortable: false, value: "actions" },
    ],

    items: [],
    total: 0,
    loading: false,
    searching: false,
    approving: false,

    tableFooter: {
      "items-per-page-options": [5, 10, 15, 20],
      "items-per-page-text": "Linhas por página",
    },

    options: {},
    filter: {},
  }),

  computed: {
    crumbs() {
      return [
        { text: "Dashboard", disabled: false, to: "/admin", exact: true },
        {
          text: "Listagem de diários de secção",
          disabled: true,
          to: window.location.href,
          exact: true,
        },
      ];
    },

    canApproveSectionDiaries() {
      return this.$store.getters.hasPermission([
        "super",
        "section_diaries.manage",
      ]);
    },
  },

  mounted() {
    if (
      !this.$store.getters.hasPermission([
        "super",
        "section_diaries.read",
        "section_diaries.manage",
      ])
    ) {
      this.$router.push("/admin");
    }

    this.fillBaseData();
  },

  methods: {
    fillBaseData() {
      const key = "section-diaries-list-" + this.$store.state.user.data.id;

      if (localStorage[key] !== undefined) {
        let data = JSON.parse(localStorage[key]);

        this.filter = data.filter || {};
        this.options = data.options || {};
      }
    },

    searchSectionDiaries(event) {
      if (!!event && event.type === "submit") {
        this.options.page = 1;
      }

      this.loading = true;

      let request = {
        sortBy: this.options.sortBy,
        sortDesc: this.options.sortDesc,
        page: this.options.page,
        limit: this.options.itemsPerPage,
      };

      Object.assign(request, { ...this.filter });

      localStorage[
        "section-diaries-list-" + this.$store.state.user.data.id
      ] = JSON.stringify({
        filter: this.filter,
        options: this.options,
      });

      SectionDiaries.search(request)
        .then((response) => {
          let data = response.data;

          this.total = data.total;
          this.items = data.data || [];
          this.searching = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    approveSectionDiary(item) {
      if (!item || !item.id || item.approved || this.approving) {
        return;
      }

      if (!this.canApproveSectionDiaries) {
        return;
      }

      this.approving = true;

      SectionDiaries.approve(item.id)
        .then(() => {
          this.searchSectionDiaries();
        })
        .finally(() => {
          this.approving = false;
        });
    },

    resetSectionDiaries() {
      this.filter = {};
      this.searchSectionDiaries();
    },

    getTableDate(date) {
      return date != null ? dayjs(date).toDate().toLocaleDateString() : "-";
    },
  },
};
</script>

<style scoped>
.eva-page {
  padding-bottom: 32px;
}

.eva-top {
  margin-top: 8px;
}

.eva-breadcrumbs :deep(.v-breadcrumbs__item) {
  font-size: 13px;
}

.eva-titlebar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.eva-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}

.eva-card__title {
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-chip {
  border-radius: 999px;
}

.eva-table {
  border-radius: 14px;
  overflow: hidden;
}

.eva-table :deep(thead th) {
  font-weight: 800;
  color: rgba(0,0,0,0.75);
  letter-spacing: 0.2px;
}

.eva-table :deep(tbody tr:hover) {
  background: rgba(0,0,0,0.02);
}
</style>